/**
 * The two ways a trade leaves this app, as one colour-and-glyph language.
 *
 * WHY IT IS A MODULE. A swap can be put to a friend IN APP — a proposal they
 * agree to, which files the trade — or exported as a CARD to a chat outside it.
 * They are different acts with different consequences, and they were two neon
 * buttons of the same colour sitting side by side. Nothing on the trade they
 * produced said which one had been used either, so a proposal in the list below
 * and a card sent over a messaging app were indistinguishable a minute later.
 *
 * Each method now owns a colour and an icon, and everything that method touches
 * wears them: the button, the chip on a trade that has been shared that way, and
 * the border of the trade itself. So the button you press and the row you get
 * are visibly the same thing.
 *
 * The colours are tokens (`--share-card` is defined light and dark in
 * index.css); in-app reuses the app's own accent, which is what proposals have
 * always been drawn in.
 */
import { Handshake, ImageDown, type LucideIcon } from "lucide-react";

export type TradeShareMethod = "inApp" | "card";

export interface TradeShareMethodMeta {
  key: TradeShareMethod;
  /** Button label on the Trade Match card. */
  label: string;
  /** Small print under the button label. */
  hint: string;
  /** Two or three words, for a chip on a trade that was shared this way. */
  chip: string;
  /** Complete CSS colour — a bare value, usable in `border` and `background`. */
  color: string;
  Icon: LucideIcon;
}

export const TRADE_SHARE_METHODS: Record<TradeShareMethod, TradeShareMethodMeta> = {
  inApp: {
    key: "inApp",
    label: "Ask to trade",
    hint: "In Nexus Dex",
    chip: "Asked in app",
    // The accent green every proposal has always been drawn in.
    color: "var(--accent-fill)",
    Icon: Handshake,
  },
  card: {
    key: "card",
    label: "Share card",
    hint: "Save or send",
    chip: "Card shared",
    color: "hsl(var(--share-card))",
    Icon: ImageDown,
  },
};

/** A colour at a given alpha — for the tints and borders each method wears. */
export function shareMethodAlpha(method: TradeShareMethod, alpha: number): string {
  return `color-mix(in srgb, ${TRADE_SHARE_METHODS[method].color} ${Math.round(alpha * 100)}%, transparent)`;
}
